export const nephronModule = {
  id: "nephron",
  title: "The Nephron: Part 1",
  sections: [
    {
      type: "text",
      title: "Nephron Overview & Types",
      content: `
        <p>The <span class="glossary-term">nephron</span> is the functional unit of the kidney. Each kidney contains approximately <strong>1–1.2 million nephrons</strong>, and this number declines with age and kidney disease.</p>

        <p>Every nephron consists of:</p>
        <ul>
          <li><strong>Renal corpuscle</strong> — glomerulus + Bowman's capsule</li>
          <li><strong>Proximal convoluted tubule (PCT)</strong></li>
          <li><strong>Loop of Henle</strong> — descending and ascending limbs</li>
          <li><strong>Distal convoluted tubule (DCT)</strong></li>
          <li><strong>Collecting duct</strong> — shared by multiple nephrons</li>
        </ul>

        <p>There are two functionally distinct types of nephron:</p>

        <div class="highlight-box">
          <p><strong>Cortical nephrons (~85%)</strong></p>
          <ul>
            <li>Glomeruli located in the <strong>outer cortex</strong></li>
            <li><strong>Short loop of Henle</strong> — dips only into the outer medulla</li>
            <li>Efferent arteriole drains into <strong>peritubular capillaries</strong></li>
            <li>Primary role: reabsorption and secretion</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Juxtamedullary nephrons (~15%)</strong></p>
          <ul>
            <li>Glomeruli located near the <strong>corticomedullary junction</strong></li>
            <li><strong>Long loop of Henle</strong> — penetrates deep into the inner medulla</li>
            <li>Efferent arteriole drains into <strong>vasa recta</strong> (hairpin capillaries)</li>
            <li>Primary role: building the medullary osmotic gradient for urine <strong>concentration</strong></li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Mnemonic: Juxtamedullary</div>
          <div class="mnemonic-content"><strong>"Just go Deep"</strong> — Juxtamedullary nephrons have long loops that go Deep into the medulla, and drain into Vasa recta (not peritubular capillaries).</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "Approximately how many nephrons does each human kidney contain?",
      options: ["100,000", "500,000", "1–1.2 million", "5 million"],
      correct: 2,
      explanation:
        "Each kidney contains approximately 1–1.2 million nephrons. This number declines with age and in chronic kidney disease, reducing renal reserve. Low nephron endowment at birth (e.g., in premature infants) is a risk factor for hypertension and CKD in later life.",
    },
    {
      type: "question",
      text: "Which type of nephron is primarily responsible for concentrating urine?",
      options: [
        "Cortical nephrons",
        "Juxtamedullary nephrons",
        "Subcapsular nephrons",
        "Midcortical nephrons",
      ],
      correct: 1,
      explanation:
        "Juxtamedullary nephrons (~15%) have long loops of Henle that penetrate deep into the inner medulla. Their associated vasa recta preserve the medullary osmotic gradient (300 → 1200 mOsm/kg), which is required to concentrate urine when ADH is present.",
    },
    {
      type: "question",
      text: "What vascular structure drains the efferent arterioles of juxtamedullary nephrons?",
      options: [
        "Peritubular capillaries",
        "Arcuate veins",
        "Vasa recta",
        "Interlobular capillaries",
      ],
      correct: 2,
      explanation:
        "Efferent arterioles of juxtamedullary nephrons form the vasa recta — hairpin-shaped capillary loops that descend alongside the long loop of Henle. Their slow, countercurrent flow preserves the medullary osmotic gradient necessary for urine concentration.",
    },
    {
      type: "text",
      title: "Blood Supply of the Nephron",
      content: `
        <p>The nephron has a <strong>unique dual capillary system</strong> — the only site in the body where two arterioles (not an arteriole and a venule) are interposed between two capillary beds:</p>

        <p><strong>Renal artery → Segmental → Interlobar → Arcuate → Interlobular → Afferent arteriole → Glomerular capillaries → Efferent arteriole → Peritubular capillaries / Vasa recta</strong></p>

        <div class="highlight-box">
          <p><strong>Afferent arteriole</strong></p>
          <ul>
            <li>Dilated by <span class="glossary-term">prostaglandins</span> (PGE₂, PGI₂) and nitric oxide</li>
            <li>Constricted by angiotensin II and adenosine (tubuloglomerular feedback)</li>
            <li>NSAIDs block prostaglandin synthesis → afferent constriction → ↓GFR (dangerous in hypovolaemia or heart failure)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Efferent arteriole</strong></p>
          <ul>
            <li>Constricted by <span class="glossary-term">angiotensin II</span> → maintains glomerular pressure and GFR when renal perfusion is reduced</li>
            <li>ACE inhibitors / ARBs block efferent constriction → ↓GFR (dangerous in bilateral renal artery stenosis)</li>
            <li>Higher resistance than afferent arteriole → generates glomerular filtration pressure</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Vasa recta</strong></p>
          <ul>
            <li>Hairpin capillaries that parallel the long loops of Henle</li>
            <li><strong>Slow, countercurrent flow</strong> preserves the medullary osmotic gradient</li>
            <li>Disruption (e.g., in sickle cell disease) → medullary ischaemia → <span class="purple-term">papillary necrosis</span></li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with heart failure on NSAIDs develops acute kidney injury. What is the primary mechanism?",
      options: [
        "Direct tubular toxicity from the NSAID metabolites",
        "Blockade of prostaglandin-mediated afferent arteriolar dilation",
        "Blockade of angiotensin II-mediated efferent constriction",
        "Increased renin secretion causing hypertension",
      ],
      correct: 1,
      explanation:
        "Prostaglandins (PGE₂, PGI₂) dilate the afferent arteriole to maintain GFR in low-perfusion states. NSAIDs block COX enzymes → ↓prostaglandins → afferent constriction → ↓GFR → pre-renal AKI. This is most dangerous in states of already reduced perfusion such as heart failure, cirrhosis, or hypovolaemia.",
    },
    {
      type: "question",
      text: "Why are ACE inhibitors contraindicated in bilateral renal artery stenosis?",
      options: [
        "They increase aldosterone levels, worsening sodium retention",
        "They block efferent arteriolar constriction, causing a sharp drop in GFR",
        "They cause direct tubular toxicity",
        "They increase renin secretion, worsening the stenosis",
      ],
      correct: 1,
      explanation:
        "In bilateral RAS, GFR is maintained by angiotensin II constricting the efferent arteriole (which maintains glomerular pressure despite reduced afferent flow). ACE inhibitors/ARBs block Ang II → efferent arteriole dilates → glomerular filtration pressure drops sharply → acute GFR loss. In unilateral RAS, the contralateral kidney compensates.",
    },
    {
      type: "question",
      text: "A patient with sickle cell disease develops frank haematuria and flank pain. Imaging shows papillary necrosis. Which vascular structure is most directly affected?",
      options: [
        "Afferent arteriole",
        "Glomerular capillaries",
        "Vasa recta",
        "Peritubular capillaries",
      ],
      correct: 2,
      explanation:
        "The vasa recta supply the renal papillae. In sickle cell disease, sickling occurs preferentially in the hypoxic, hyperosmolar, low-pH environment of the medulla → vasa recta occlusion → papillary ischaemia and necrosis. This also impairs urine concentration, causing hyposthenuria. Other causes of papillary necrosis: analgesic nephropathy, diabetes, obstructive uropathy (mnemonic: SAND).",
    },
    {
      type: "text",
      title: "Glomerular Filtration: Size & Charge Selectivity",
      content: `
        <p>The glomerulus filters approximately <strong>180 litres of plasma per day</strong> (GFR ~125 mL/min). The three-layer filtration barrier determines what enters the Bowman's space.</p>

        <div class="highlight-box">
          <p><strong>Size selectivity</strong></p>
          <ul>
            <li>MW &lt;7,000 Da — freely filtered (e.g., inulin 5,200 Da, creatinine 113 Da)</li>
            <li>MW 7,000–70,000 Da — partially filtered</li>
            <li>MW &gt;70,000 Da — not filtered (e.g., IgM, fibrinogen)</li>
            <li>Albumin (69,000 Da) — minimally filtered (also repelled by negative charge)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Charge selectivity</strong></p>
          <ul>
            <li>GBM is rich in <span class="glossary-term">heparan sulphate</span> — negatively charged</li>
            <li>Repels negatively charged molecules, including albumin</li>
            <li><strong>Minimal change disease</strong> — loss of charge selectivity → selective proteinuria (albumin predominantly)</li>
            <li><strong>Membranous nephropathy</strong> — structural GBM damage → non-selective (mixed) proteinuria</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Filtration fraction (FF)</strong></p>
          <ul>
            <li>FF = GFR / RPF = 125 / 625 = <strong>~20%</strong></li>
            <li>↑FF with efferent constriction (Ang II) or ↓RPF</li>
            <li>↑FF → ↑oncotic pressure in peritubular capillaries → ↑tubular reabsorption</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A child presents with nephrotic syndrome. Biopsy shows normal light microscopy but podocyte foot process effacement on electron microscopy. What is the mechanism of proteinuria?",
      options: [
        "Loss of size selectivity due to GBM thickening",
        "Loss of charge selectivity due to reduction in heparan sulphate",
        "Structural GBM rupture allowing large proteins through",
        "Mesangial IgA deposits blocking the filtration barrier",
      ],
      correct: 1,
      explanation:
        "Minimal change disease causes loss of the negative charge (heparan sulphate) on the GBM. Since albumin is also negatively charged, it is normally electrostatically repelled. Loss of charge selectivity allows albumin through → selective proteinuria. Light microscopy is normal; EM shows podocyte foot process effacement. It is the most common cause of nephrotic syndrome in children and responds to corticosteroids.",
    },
    {
      type: "question",
      text: "The normal filtration fraction (GFR/RPF) is approximately:",
      options: ["5%", "10%", "20%", "40%"],
      correct: 2,
      explanation:
        "The filtration fraction = GFR / RPF = 125 mL/min / 625 mL/min ≈ 20%. This means 20% of plasma entering the glomerulus is filtered. The filtration fraction increases when angiotensin II constricts the efferent arteriole (e.g., in low-perfusion states), which also increases oncotic pressure in the peritubular capillaries, favouring tubular reabsorption.",
    },
    {
      type: "question",
      text: "Which substance is the gold standard marker for measuring true GFR, and why?",
      options: [
        "Creatinine — because it is endogenously produced and easy to measure",
        "PAH — because it is completely secreted and measures renal plasma flow",
        "Inulin — because it is freely filtered and neither secreted nor reabsorbed",
        "Urea — because it reflects nitrogen balance",
      ],
      correct: 2,
      explanation:
        "Inulin (MW ~5,200 Da) is the gold standard. It is freely filtered, not protein-bound, and neither secreted nor reabsorbed by the tubules, so its clearance exactly equals GFR. Creatinine overestimates GFR by ~10–15% due to PCT secretion. PAH clearance measures renal plasma flow (RPF), not GFR, because it is both filtered and completely secreted.",
    },
    {
      type: "text",
      title: "Proximal Convoluted Tubule (PCT)",
      content: `
        <p>The PCT reabsorbs the <strong>largest amount of filtered solute and water</strong> of any nephron segment — approximately <strong>65% of filtered Na⁺, water, and HCO₃⁻</strong>, and 100% of filtered glucose and amino acids.</p>

        <p>Reabsorption in the PCT is <strong>isosmotic</strong> — water follows solute in proportion, so tubular fluid osmolality remains ~300 mOsm throughout.</p>

        <div class="highlight-box">
          <p><strong>Key apical transporters</strong></p>
          <ul>
            <li><strong>SGLT2</strong> — Na⁺/glucose cotransporter; has a transport maximum (Tm ~180 mg/dL plasma glucose)</li>
            <li><strong>NHE3</strong> — Na⁺/H⁺ exchanger; reabsorbs Na⁺, secretes H⁺ → drives HCO₃⁻ reabsorption via carbonic anhydrase</li>
            <li><strong>Na⁺-amino acid cotransporters</strong> — reabsorbs all filtered amino acids</li>
            <li><strong>NaPi-2</strong> — Na⁺-phosphate cotransporter; inhibited by PTH → phosphaturia</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>PCT secretion</strong></p>
          <ul>
            <li>Secretes organic acids (uric acid, drugs such as penicillin, methotrexate), creatinine, and H⁺</li>
            <li>Creatinine secretion explains why creatinine clearance slightly <em>overestimates</em> true GFR</li>
            <li>Probenecid blocks uric acid secretion → used to treat gout (reduces urate levels)</li>
          </ul>
        </div>

        <p>Above the renal glucose threshold (~10 mmol/L), SGLT2 is saturated → glucose spills into urine (<span class="purple-term">glycosuria</span>). <strong>SGLT2 inhibitors</strong> (gliflozins) deliberately block this transporter to lower blood glucose and provide renoprotection in CKD and heart failure.</p>
      `,
    },
    {
      type: "question",
      text: "A type 2 diabetic patient has a plasma glucose of 16 mmol/L and glycosuria. Which transporter is responsible for glucose reabsorption in the PCT, and why is it failing?",
      options: [
        "GLUT2 — overwhelmed by high luminal glucose concentration",
        "SGLT2 — saturated above its transport maximum (Tm)",
        "NHE3 — inhibited by high glucose",
        "Na⁺/K⁺-ATPase — downregulated by insulin resistance",
      ],
      correct: 1,
      explanation:
        "SGLT2 (sodium-glucose cotransporter 2) on the apical surface of the PCT cotransports Na⁺ and glucose. It has a transport maximum (Tm) of ~180 mg/dL (10 mmol/L). Above this threshold, SGLT2 is saturated and excess glucose remains in the tubular lumen → glycosuria. SGLT2 inhibitors (e.g., empagliflozin, dapagliflozin) deliberately block this to lower blood glucose, and also reduce intraglomerular pressure, providing renoprotection.",
    },
    {
      type: "question",
      text: "Why does creatinine clearance slightly overestimate true GFR?",
      options: [
        "Creatinine is partially reabsorbed by the PCT",
        "Creatinine is secreted by the PCT in addition to being freely filtered",
        "Creatinine is protein-bound and cannot be freely filtered",
        "Creatinine is metabolised within the tubular lumen",
      ],
      correct: 1,
      explanation:
        "Creatinine is freely filtered at the glomerulus AND additionally secreted by the PCT. Therefore, creatinine clearance = GFR + secreted creatinine, which exceeds true GFR by ~10–15%. Despite this, creatinine clearance remains a useful clinical approximation. In contrast, inulin clearance = true GFR because inulin is neither secreted nor reabsorbed.",
    },
    {
      type: "question",
      text: "PTH acts on the PCT to cause phosphaturia. What is the direct mechanism?",
      options: [
        "PTH activates NHE3, increasing phosphate secretion",
        "PTH inhibits NaPi-2 cotransporters, reducing phosphate reabsorption",
        "PTH opens ROMK channels, allowing phosphate efflux",
        "PTH increases SGLT2 activity, causing competitive inhibition of phosphate",
      ],
      correct: 1,
      explanation:
        "PTH inhibits the NaPi-2 (sodium-phosphate) cotransporter on the apical surface of the PCT via a cAMP-mediated pathway, causing internalisation of the transporter. This reduces phosphate reabsorption → phosphaturia → hypophosphataemia. PTH simultaneously promotes calcium reabsorption in the DCT and stimulates 1α-hydroxylase in the kidney to activate vitamin D.",
    },
    {
      type: "text",
      title: "Fanconi Syndrome",
      content: `
        <p><span class="glossary-term">Fanconi syndrome</span> is a generalised dysfunction of the proximal convoluted tubule, resulting in failure to reabsorb multiple filtered substances that are normally fully reclaimed.</p>

        <div class="highlight-box">
          <p><strong>Features of Fanconi syndrome</strong></p>
          <ul>
            <li><strong>Glycosuria</strong> — despite normal blood glucose (normoglycaemic glycosuria)</li>
            <li><strong>Aminoaciduria</strong> — all amino acids lost</li>
            <li><strong>Phosphaturia</strong> → hypophosphataemia → <span class="purple-term">osteomalacia / rickets</span></li>
            <li><strong>Uricosuria</strong> → hypouricaemia</li>
            <li><strong>Type 2 (proximal) RTA</strong> — failure to reabsorb HCO₃⁻ → hyperchloraemic metabolic acidosis</li>
            <li><strong>Hypokalaemia</strong> and polyuria</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Causes — mnemonic "WWCTTGF"</strong></p>
          <ul>
            <li><strong>W</strong>ilson's disease (copper deposition)</li>
            <li><strong>W</strong>aldenstrom's / Multiple <strong>M</strong>yeloma (light chain toxicity)</li>
            <li><strong>C</strong>ystinosis (commonest inherited cause in children)</li>
            <li><strong>T</strong>enofovir (antiretroviral — mitochondrial toxicity to PCT)</li>
            <li><strong>T</strong>oxins — lead, cadmium, mercury</li>
            <li><strong>G</strong>alactosaemia / hereditary fructose intolerance</li>
            <li><strong>F</strong>anconi-Bickel syndrome (GLUT2 mutation)</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Mnemonic: Features of Fanconi</div>
          <div class="mnemonic-content"><strong>"GAPU + Type 2 RTA"</strong> — Glycosuria, Aminoaciduria, Phosphaturia, Uricosuria, plus Type 2 RTA. All from generalised PCT dysfunction.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with HIV on tenofovir presents with bone pain and is found to have glycosuria with normal blood glucose, aminoaciduria, and hypophosphataemia. What syndrome does this represent?",
      options: [
        "Nephrotic syndrome",
        "Renal tubular acidosis type 1",
        "Fanconi syndrome",
        "SIADH",
      ],
      correct: 2,
      explanation:
        "This is Fanconi syndrome — generalised PCT dysfunction causing failure to reabsorb glucose (normoglycaemic glycosuria), amino acids, phosphate (→ osteomalacia/bone pain), uric acid, and HCO₃⁻ (type 2 RTA). Tenofovir is a well-recognised cause through mitochondrial toxicity to PCT cells. Other causes include cystinosis, Wilson's disease, multiple myeloma, and heavy metal toxicity.",
    },
    {
      type: "question",
      text: "In Fanconi syndrome, what type of renal tubular acidosis (RTA) develops, and why?",
      options: [
        "Type 1 RTA — distal collecting duct H⁺ secretion failure",
        "Type 2 RTA — failure to reabsorb HCO₃⁻ in the PCT",
        "Type 4 RTA — aldosterone deficiency or resistance",
        "No RTA — the acidosis is purely from phosphate wasting",
      ],
      correct: 1,
      explanation:
        "Fanconi syndrome impairs PCT function, including the NHE3/carbonic anhydrase system responsible for HCO₃⁻ reabsorption (normally 85% of filtered HCO₃⁻). This causes Type 2 (proximal) RTA — HCO₃⁻ is lost in urine → hyperchloraemic, normal anion gap metabolic acidosis. Unlike Type 1 RTA, patients can acidify urine below pH 5.5 when plasma HCO₃⁻ falls below the reduced threshold.",
    },
  ],
};
